<template>
  <div :class="[active ? 'opacity-1' : 'opacity-0 not-visible']" class="fixed z-50 top-0 left-0 w-full h-full bg-black/20 flex items-center justify-center transition-all duration-300">
    <div :class="[active ? 'translate-y-0' : 'translate-y-full']" class="rounded-lg flex flex-col flex-grow max-h-full bg-white shadow-xl w-full max-w-lg overflow-hidden mt-auto md:mt-0 transition-transform duration-300">
      <div class="h-full overflow-y-auto divide-y">
        <header class="py-4 px-4">
          <h3 class="font-sans text-2xl uppercase">Gestionar pase</h3>
        </header>
        <div class="p-4 flex items-center justify-between">
          <div class="leading-tight">
            <p>Este pase está <strong>{{ pass | status }}</strong></p>
          </div>
          <div>
            <el-switch v-model="isActive"></el-switch>
          </div>
        </div>
        <div class="p-4">
          <p>{{ pass.description }}</p>
        </div>
        <div class="p-4">
          <h4 class="font-bold text-sm text-slate-600">Acompañante</h4>
          <p>{{ pass.name }}</p>
        </div>
        <div class="p-4">
          <h4 class="font-bold text-sm text-slate-600">Socios</h4>
          <p>{{ pass | members }}</p>
        </div>
        <biometric-access-enabled v-if="biometricStatus === STATUS.FINISHED" title="Acceso para el acompañante" @revoke="openRevoke"></biometric-access-enabled>
        <biometric-access-pending v-else-if="biometricStatus === STATUS.IN_PROCESS"></biometric-access-pending>
        <biometric-access-error v-else-if="biometricStatus === STATUS.ERROR" @reset="onReset"></biometric-access-error>
        <biometric-face-recognition-config v-else @start="openPhoto"></biometric-face-recognition-config>
      </div>
      <footer class="flex-shrink-0 flex items-center justify-between p-4">
        <base-button mode="secondary-phantom" text="Cancelar" @click="close" />
        <base-button mode="primary" text="Modificar" :loading="loading" @click="update" />
      </footer>
    </div>

    <base-modal v-model="revokeModal">
      <template v-slot:title>Revocación acceso biometríco</template>
      <p>Va a proceder a revocar el consentimiento del acceso biométrico a las instalaciones. Podrá volver a iniciar este proceso en cualquier momento.</p>
      <p class="mt-3">¿Desea continuar con la revocación del acceso biométrico?</p>
      <template v-slot:actions>
        <base-button mode="secondary-phantom" text="Cancelar" @click="closeRevoke" />
        <el-button @click="revokeBiometricProcess" type="danger" :loading="loading">Sí, revocar</el-button>
      </template>
    </base-modal>

    <photo-modal :active.sync="photoModal" @image="onImage" />
  </div>
</template>

<script>
import PhotoModal from '@/components/PhotoModal.vue'
import PassServices from '@/services/passes'
import Pass from '@/utils/Pass'
import BiometricAccessEnabled from '@/components/biometric/BiometricAccessEnabled'
import BiometricAccessPending from '@/components/biometric/BiometricAccessPending'
import BiometricAccessError from '@/components/biometric/BiometricAccessError'
import BiometricFaceRecognitionConfig from '@/components/biometric/BiometricFaceRecognitionConfig'
import STATUS from '@/config/status'
import ImageWrapper from '@/utils/Image'

export default {
  name: 'PassSettingsModal',
  components: {
    PhotoModal,
    BiometricAccessEnabled,
    BiometricAccessPending,
    BiometricAccessError,
    BiometricFaceRecognitionConfig
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    pass: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      revokeModal: false,
      photoModal: false,
      isActive: true,
      facial: true,
      loading: false,
      biometricStatus: null,
      STATUS,
      reset: false
    }
  },
  methods: {
    close: function () {
      this.$emit('update:active', false)
    },
    fetchBiometricStatus: async function () {
      try {
        this.loading = true
        let biometricStatus = await PassServices.statusBiometric(this.pass.id)
        if (biometricStatus === STATUS.ERROR && this.reset) {
          biometricStatus = STATUS.NOT_CONFIGURED
        }
        this.biometricStatus = biometricStatus
      } catch (error) {
        this.doFeedback()
      } finally {
        this.loading = false
      }
    },
    update: async function () {
      try {
        this.loading = true
        await PassServices.updateStatus(this.pass.id, this.isActive)
        this.$emit('update')
      } catch (error) {
        this.doFeedback()
      } finally {
        this.loading = false
      }
    },
    openRevoke: function () {
      this.revokeModal = true
    },
    openPhoto: function () {
      this.photoModal = true
    },
    closeRevoke: function () {
      this.revokeModal = false
    },
    onReset: function () {
      this.reset = true
    },
    onImage: function (image) {
      this.photoModal = false
      const img = ImageWrapper.removeMimeFromBase64(image)
      this.acceptBiometricProcess(img)
    },
    acceptBiometricProcess: async function (image) {
      try {
        this.$store.commit('app/setLoading', true)
        await PassServices.processBiometric(this.pass.id, image)
        this.biometricStatus = await PassServices.statusBiometric(this.pass.id)
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('app/setLoading', false)
      }
    },
    revokeBiometricProcess: async function () {
      try {
        this.loading = true
        await PassServices.revokeBiometric(this.pass.id)
        this.biometricStatus = await PassServices.statusBiometric(this.pass.id)
      } catch (error) {
        this.doFeedback()
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    pass: {
      immediate: true,
      handler: function (value) {
        this.isActive = value.status === 'active'
        this.fetchBiometricStatus()
      }
    }
  },
  filters: {
    status: function (value) {
      return Pass.getStatusName(value?.status)
    },
    members: function (value) {
      return value?.members ? value.members.map(member => member.name).join(', ') : null
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
